import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Wrench, Sparkles, ArrowDownRight } from "lucide-react";
import { IMAGES } from "../../data/content";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { scrollToId } from "../../lib/scroll";

const highlights = [
    { icon: ShieldCheck, title: "Preventive Dental Care", desc: "Regular check-ups and cleaning that keep problems from starting." },
    { icon: Wrench, title: "Restorative Treatment", desc: "Fillings, root canals, crowns and implants that rebuild function." },
    { icon: Sparkles, title: "Smile Improvement", desc: "Whitening and cosmetic care for a smile you feel proud of." },
];

export const Featured = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [-50, 50]);

    return (
        <section ref={ref} data-testid="featured-section" className="relative overflow-hidden bg-ink py-24 text-[#f8f6f0] lg:py-32">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -left-40 top-1/3 h-[36rem] w-[36rem] rounded-full bg-[radial-gradient(circle,rgba(45,212,191,0.12),transparent_65%)]"
            />
            <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-2 lg:px-10">
                <div>
                    <SectionHeading
                        number="03"
                        eyebrow="Featured Care"
                        title="Complete Care for a"
                        accent="Healthier Smile"
                        dark
                        sub="One clinic for the full journey of your oral health — protect, restore and refine."
                    />
                    <div className="mt-10 space-y-4">
                        {highlights.map((h, i) => (
                            <Reveal key={h.title} delay={i * 0.1}>
                                <div
                                    data-testid={`featured-highlight-${i}`}
                                    className="group flex items-start gap-5 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm transition-[background-color,border-color] duration-500 hover:border-teal-300/40 hover:bg-white/10"
                                >
                                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-teal-300/15 text-teal-300">
                                        <h.icon className="h-5 w-5" />
                                    </span>
                                    <div>
                                        <h3 className="font-display text-xl">{h.title}</h3>
                                        <p className="mt-1 text-sm leading-relaxed text-slate-300">{h.desc}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.3}>
                        <button
                            data-testid="featured-book-consultation-btn"
                            onClick={() => scrollToId("#appointment")}
                            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-teal-accent px-8 py-4 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-teal-500"
                        >
                            Book a Consultation
                            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                        </button>
                    </Reveal>
                </div>

                <Reveal className="relative">
                    <div className="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]">
                        <motion.img
                            src={IMAGES.featured}
                            alt="Precision dental instruments prepared for treatment"
                            loading="lazy"
                            style={{ y: imgY, scale: 1.12 }}
                            className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
                        <p className="absolute bottom-6 left-6 right-6 font-display text-2xl font-light italic text-white/90">
                            “A calm visit, a clear plan, a healthier smile.”
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
